import utility from '../utility'
import ruler from '../ruler'
import ganalytics from '../ganalytics'
import OpenSeadragon from 'openseadragon'
import navigator from '../navigator'
import client from '../apiClient'
import uiConfiguration from '../uiConfiguration'
import internalAnalyticsClient from '../internalAnalyticsClient'
import loader from '../loader'
import { setupFullscreenButton } from './fullscreen'
import viewerOnPremises from './viewerOnPremises'
import viewersManipulation from './viewersManipulation'
import { mountViewerCreditsSlot } from '../viewerCredits'

require('../openseadragon-svg-overlay')

let loaded = false
let mainViewerIframe = null

function throttle(callback, interval) {
  let enableCall = true

  return function (...args) {
    if (!enableCall) return

    enableCall = false
    callback.apply(this, args)
    setTimeout(() => (enableCall = true), interval)
  }
}

function updateWidgetData(viewPort, sourceUrl) {
  let bounds = viewPort.getBounds(true)
  const widgetContainer = document.getElementById('widget-data')
  if (!widgetContainer) {
    return
  }
  let topLeft = viewPort.viewportToImageCoordinates(bounds.getTopLeft())
  widgetContainer.setAttribute('data-image-topLeft-x', topLeft.x)
  widgetContainer.setAttribute('data-image-topLeft-y', topLeft.y)
  let bottomRight = viewPort.viewportToImageCoordinates(bounds.getBottomRight())
  widgetContainer.setAttribute('data-image-bottomRight-x', bottomRight.x)
  widgetContainer.setAttribute('data-image-bottomRight-y', bottomRight.y)
  const uuid = utility.extractUuidFromTileSourceUrl(sourceUrl)
  widgetContainer.setAttribute('data-image-uuid', uuid)

  widgetContainer.setAttribute('data-viewerBounds-topLeft-x', bounds.x)
  widgetContainer.setAttribute('data-viewerBounds-topLeft-y', bounds.y)
  widgetContainer.setAttribute('data-viewerBounds-width', bounds.width)
  widgetContainer.setAttribute('data-viewerBounds-height', bounds.height)

  const x = bounds.x + bounds.width / 2
  const y = bounds.y + bounds.height / 2
  let zoom = viewPort.getZoom()
  widgetContainer.setAttribute('data-position-parameters', `&zoom=${zoom}&x=${x}&y=${y}`)

  let widgetData = {
    type: 'widgetData',
    imageCoordinates: {
      topLeft: topLeft,
      bottomRight: bottomRight
    },
    viewportCoordinates: {
      x: bounds.x,
      y: bounds.y,
      w: bounds.width,
      h: bounds.height
    },
    position: {
      centerX: x,
      centerY: y,
      zoom: zoom
    },
    uuid: uuid
  }

  postToParent(widgetData)
}

function postToParent(payload) {
  if (window.parent !== undefined) {
    window.parent.postMessage(payload, window.frameTargetOrigin !== undefined ? window.frameTargetOrigin : '*')
  }
}

function sendToParent(event) {
  try {
    if (window.location.hash) {
      event.sender = window.location.hash
    }
    parent.dispatchEvent(event)
  } catch (corsNotAllowed) {}
}

function getPosition(id, state) {
  if (!state[id].visible) {
    return 1000
  }

  let notVisibleCount = 0
  for (let i = 0; i < state.length; i++) {
    if (!state[i].visible) {
      notVisibleCount++
    }
    if (id === state[i].id) {
      return id - notVisibleCount
    }
  }
}

function ensureFocusOnViewerToAllowKeyboardInteraction(viewer, viewerId) {
  viewer.addHandler('open', () => {
    document.getElementById(viewerId).querySelector('.openseadragon-canvas').focus()
  })
}

function sendWidgetOpenToParentOnlyAtFirstLoad() {
  if (loaded) {
    return
  }
  loaded = true
  sendToParent(new Event('widget-open'))
}

function updateButtonsState(currentPage, numberOfImages) {
  const prevPageButton = document.getElementById('prevPage')
  const nextPageButton = document.getElementById('nextPage')
  const goToStartButton = document.getElementById('goToStartButton')
  const goToEndButton = document.getElementById('goToEndButton')

  prevPageButton.classList.remove('disabled')
  nextPageButton.classList.remove('disabled')
  goToStartButton.classList.remove('disabled')
  goToEndButton.classList.remove('disabled')

  if (currentPage <= 0) {
    prevPageButton.classList.add('disabled')
    goToStartButton.classList.add('disabled')
  }
  if (currentPage >= numberOfImages - 1) {
    nextPageButton.classList.add('disabled')
    goToEndButton.classList.add('disabled')
  }

  // disabling prev/next buttons if image is first/last
  let buttons = [prevPageButton, nextPageButton]
  buttons.forEach(button => {
    if (button.classList.contains('disabled')) {
      button.querySelector('button').disabled = true
    } else {
      button.querySelector('button').disabled = false
    }
  })
}

function updateLoader() {
  resetLoader()
  const viewer = `
    <div class="loaderContainer" id="loaderContainer">
  <div id="loader" class="lds-roller"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div></div>
  `
  const widgetContainer = document.getElementById('widgetContainer')
  widgetContainer.insertAdjacentHTML('beforeend', viewer)
}

function resetLoader() {
  const element = document.querySelectorAll('.loaderContainer')
  if (element != null) {
    element.forEach(box => {
      box.remove()
    })
  }
}

function setupNavigationButtons(viewer) {
  const numberOfImages = viewer.tileSources.length

  const nextPageButton = document.getElementById('nextPage')
  nextPageButton.addEventListener('click', () => {
    const currentPageIndex = viewer.currentPage()
    const nextPage = currentPageIndex + 1
    updateLoader()
    if (nextPage < numberOfImages) {
      viewer.goToPage(nextPage)
    }
    updateButtonsState(viewer.currentPage(), numberOfImages)
  })

  const prevPageButton = document.getElementById('prevPage')
  prevPageButton.addEventListener('click', () => {
    const currentPageIndex = viewer.currentPage()
    const prevPage = currentPageIndex - 1
    updateLoader()
    if (prevPage >= 0) {
      viewer.goToPage(prevPage)
    }

    updateButtonsState(viewer.currentPage(), numberOfImages)
  })

  const goToStartButton = document.getElementById('goToStartButton')
  goToStartButton.addEventListener('click', () => {
    updateLoader()
    viewer.goToPage(0)
    updateButtonsState(viewer.currentPage(), numberOfImages)
  })

  const goToEndButton = document.getElementById('goToEndButton')
  goToEndButton.addEventListener('click', () => {
    updateLoader()
    viewer.goToPage(numberOfImages - 1)
    updateButtonsState(viewer.currentPage(), numberOfImages)
  })

  const toggleIndexButton = document.getElementById('toggleIndex')
  const indexStrip = document.getElementById('indexStrip')
  let wasNavigatorHiddenBeforeIndexStripOpen = false
  toggleIndexButton.addEventListener('click', () => {
    const isOpeningIndexStrip = indexStrip.classList.contains('hidden')
    if (isOpeningIndexStrip) {
      const navigatorWithButtons = document.getElementById('navigatorWithButtons')
      wasNavigatorHiddenBeforeIndexStripOpen = navigatorWithButtons?.getAttribute('doNotShow') === 'true'
    }

    toggleIndexButton.classList.toggle('active')
    indexStrip.classList.toggle('hidden')

    const isIndexStripVisible = !indexStrip.classList.contains('hidden')
    if (isIndexStripVisible) {
      navigator.hide()
    } else if (!wasNavigatorHiddenBeforeIndexStripOpen) {
      navigator.show()
    }
  })

  document.querySelectorAll('.thumbnailLink').forEach((thumbnailDiv, page) => {
    thumbnailDiv.addEventListener('click', () => {
      updateLoader()
      viewer.goToPage(page)
      updateButtonsState(viewer.currentPage(), numberOfImages)
    })
  })

  const pagesThumbnails = document.getElementById('pagesThumbnails')
  pagesThumbnails.addEventListener('wheel', event => {
    event.preventDefault()
    pagesThumbnails.scrollLeft += event.deltaY
  })
}

function getRealWidth(element) {
  return (
    element.offsetWidth +
    parseInt(window.getComputedStyle(element).marginRight) +
    parseInt(window.getComputedStyle(element).marginLeft)
  )
}

function updateItemProgress(page, numberOfPages) {
  const itemProgress = document.getElementById('itemProgress')

  const currentProgress = ((page + 1) / numberOfPages) * 100
  itemProgress.style['width'] = `${currentProgress}%`
}

function setIndexStripCurrentPage(page) {
  document.getElementById('indexStripPosition').textContent = `${page}`
  document.querySelectorAll('.thumbnailLink').forEach((thumbnailLink, index) => {
    thumbnailLink.classList.remove('selected')
    if (index + 1 === page) {
      thumbnailLink.classList.add('selected')
    }
  })

  const thumbnailsDiv = document.getElementById('pagesThumbnails')
  const thumbnailDiv = document.getElementsByClassName('thumbnailLink')[0]

  const singleThumbWidth = getRealWidth(thumbnailDiv)
  const allThumbsWidth = getRealWidth(thumbnailsDiv)

  const isOverflowingRight = page * singleThumbWidth > thumbnailsDiv.scrollLeft + thumbnailsDiv.clientWidth

  const isOverflowingLeft = (page - 1) * singleThumbWidth < thumbnailsDiv.scrollLeft
  const numberOfVisibleThumbnails = Math.ceil(allThumbsWidth / singleThumbWidth)
  if (isOverflowingRight || isOverflowingLeft) {
    const centeredScrollValue = singleThumbWidth * page - allThumbsWidth / 2 - singleThumbWidth / 2 + 8

    thumbnailsDiv.scrollTo({
      left: centeredScrollValue,
      behavior: 'smooth'
    })
  }
}

try {
  const parentDocument = parent.document
  if (parentDocument) {
    let exportRegionBtn = parentDocument.querySelector('[data-test-id="open-share-dialog"]')
    if (exportRegionBtn) {
      exportRegionBtn.addEventListener('click', () => {
        return exportRegionUrl(mainViewerIframe)
      })
    }
  }
} catch (error) {
  console.error('An error occurred:', error.message)
}

function exportRegionUrl(viewer) {
  event.stopPropagation()
  if (!viewer) return

  let bounds = viewer.viewport.getBounds()
  let imageWidth = viewer.source.width
  let imageHeight = viewer.source.height
  let mul, regionX, regionY, regionW, regionH, uuid, xywh

  if (imageWidth > imageHeight) {
    // Inverse of original operation for wider images
    mul = imageWidth / imageHeight
    regionX = (bounds.x * imageWidth) / mul
    regionY = bounds.y * imageHeight
    regionW = (bounds.width * imageWidth) / mul
    regionH = bounds.height * imageHeight
  } else {
    // Inverse of original operation for taller images
    mul = imageHeight / imageWidth
    regionX = bounds.x * imageWidth
    regionY = (bounds.y * imageHeight) / mul
    regionW = bounds.width * imageWidth
    regionH = (bounds.height * imageHeight) / mul
  }

  uuid = getUuidFromIndex(viewer.data.tileSources, viewer._sequenceIndex)
  xywh = `${regionX.toFixed(0)},${regionY.toFixed(0)},${regionW.toFixed(0)},${regionH.toFixed(0)}`
  const exportUrl = `${window.location.origin}/images/viewers/dzi/?uuid=${uuid}&xywh=${xywh}`
  window.parent.postMessage({ url: exportUrl }, '*')
}
function getUuidFromIndex(tileSources, index) {
  const element = tileSources[index] || null
  return extractUuidFromUrl(element)
}
function extractUuidFromUrl(url) {
  const uuidPattern = /[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}/
  const match = url.match(uuidPattern)
  return match ? match[0] : null
}

export default {
  createViewerDom: function (data, largeIcons, tenantViewerAssets) {
    const percentage = data.percentage

    let navigatorDOM = data.isMainViewer ? navigator.createDom(largeIcons) : ''
    const viewer = `
<div id="loader${data.viewerId}" class="lds-roller"><div></div><div></div><div></div><div></div><div></div><div></div><div></div><div></div></div>
<div class="viewer" id="${data.viewerId}" style="width:${percentage}%; left:${data.left}%" data-id="${data.id}">
</div>`
    const toolbarAndLogo = data.isMainViewer
      ? `
							${navigatorDOM}
`
      : ''
    const view = `${viewer}${toolbarAndLogo}`
    const widgetContainer = document.getElementById('widgetContainer')
    widgetContainer.insertAdjacentHTML('beforeend', view)

    if (data.isMainViewer && tenantViewerAssets) {
      mountViewerCreditsSlot({
        target: widgetContainer,
        assets: tenantViewerAssets,
        largeIcons,
        viewerId: data.viewerId
      })
    }
  },
  createViewer: function (
    data,
    customUIName,
    largeIcons,
    initialPosition = viewersManipulation.DEFAULT_INITIAL_POSITION
  ) {
    const maxZoomPixelRatio = data.maxZoomLevelPercentage / 100
    let firstViewer = data.isMainViewer

    const queryString = window.location.search
    const urlParams = new URLSearchParams(queryString)
    const imageToShowUuid = urlParams.get('uuid') || null
    let initialPageIndex = urlParams.get('index') || 0

    function checkUuidInUrl(url, uuid) {
      if (url && uuid) {
        // Create a regex using the uuid that must fully match the uuid in the tilesource's url
        const pattern = new RegExp(uuid.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&') + '(?=([^\\w-]|$))')
        return pattern.test(url)
      }
    }

    // If there is valid uuid & regex match update index of viewer's initial page, else default
    let itemFound = false
    data.tileSources.forEach((element, index) => {
      if (itemFound) return
      const uuidMatched = checkUuidInUrl(element, imageToShowUuid)
      if (imageToShowUuid && uuidMatched) {
        initialPageIndex = index
        itemFound = true
      }
    })

    const viewer = OpenSeadragon({
      id: data.viewerId,
      prefixUrl: 'openseadragon/',
      initialPage: initialPageIndex,
      tileSources: data.tileSources,
      sequenceMode: true,
      showReferenceStrip: false,
      referenceStripScroll: 'vertical',
      homeButton: 'home',
      fullPageButton: null,
      animationTime: 1.75,
      maxZoomPixelRatio: maxZoomPixelRatio,
      homeFillsViewer: false,
      gestureSettingsMouse: {
        flickEnabled: false,
        clickToZoom: false,
        dblClickToZoom: true
      },
      showNavigator: firstViewer,
      ppi: data.ppi,
      navigatorHeight: largeIcons ? '279px' : '184px',
      navigatorWidth: largeIcons ? '287px' : '184px',
      navigatorId: firstViewer ? `navigator` : null,
      zoomInButton: 'zoom-in',
      zoomOutButton: 'zoom-out',
      preserveImageSizeOnResize: true,
      loadTilesWithAjax: true,
      rotateRightButton: firstViewer && data.canRotate ? 'rotate' : null,
      rotationIncrement: data.canRotate ? 45 : 0,
      showRotationControl: true,
      data: data
    })

    let exportRegionViewer = viewer.element.baseURI.includes('mainViewerIframe')
    if (exportRegionViewer) mainViewerIframe = viewer
    setupNavigationButtons(viewer)
    updateItemProgress(initialPageIndex, viewer.tileSources.length)

    viewer.addHandler('open', function () {
      let x
      let y
      let w
      let h
      const urlParams = new URLSearchParams(queryString)
      const regionCoordinates = urlParams.get('xywh') || null

      if (regionCoordinates) {
        const regex = /([-\d]+(?:\.\d+)?),([-\d]+(?:\.\d+)?),([-\d]+(?:\.\d+)?),([-\d]+(?:\.\d+)?)/
        const match = regionCoordinates.match(regex)

        if (match) {
          x = match[1]
          y = match[2]
          w = match[3]
          h = match[4]
        }

        if ([x, y, w, h].every(x => x != undefined)) {
          let mul = 1
          let newBounds
          let imageWidth = viewer.source.width
          let imageHeight = viewer.source.height

          // bounds must be multiplied to the source measures in the correct order
          // x and w -> source width
          // y and h -> source height
          if (imageWidth > imageHeight) {
            mul = imageWidth / imageHeight
            newBounds = new OpenSeadragon.Rect(
              (x * mul) / imageWidth,
              y / imageHeight,
              (w * mul) / imageWidth,
              h / imageHeight
            )
          } else {
            mul = imageHeight / imageWidth
            newBounds = new OpenSeadragon.Rect(
              x / imageWidth,
              (y * mul) / imageHeight,
              w / imageWidth,
              (h * mul) / imageHeight
            )
          }

          document.querySelector('.openseadragon-canvas').classList.toggle('on-specific-region')
          return viewer.viewport.fitBounds(newBounds, false)
        }
      }

      resetLoader()
    })

    viewer.addHandler('page', event => {
      setIndexStripCurrentPage(event.page + 1)
      updateItemProgress(event.page, viewer.tileSources.length)
      const selectImageEvent = new CustomEvent('selectImage', {
        detail: {
          imageIndex: event.page
        }
      })
      sendToParent(selectImageEvent)
    })

    window.addEventListener('selectedImageRequired', event => {
      const selectImageEvent = new CustomEvent('selectImage', {
        detail: {
          imageIndex: viewer.currentPage()
        }
      })
      sendToParent(selectImageEvent)
    })
    //    ensureFocusOnViewerToAllowKeyboardInteraction(viewer, data.viewerId)

    const shouldSendInternalAnalytics = uiConfiguration.getConfigurationFor(customUIName, 'sendInternalAnalytics')

    if (shouldSendInternalAnalytics) {
      viewer.addHandler('open', event => {
        internalAnalyticsClient.open(
          data.uuid,
          event.eventSource.viewport.viewportToImageRectangle(event.eventSource.viewport.getBounds(true))
        )
      })

      viewer.addHandler(
        'animation',
        throttle(
          event =>
            internalAnalyticsClient.view(
              data.uuid,
              event.eventSource.viewport.viewportToImageRectangle(event.eventSource.viewport.getBounds(true))
            ),
          1000
        )
      )

      window.onunload = () => {
        internalAnalyticsClient.close(data.uuid)
      }
    }

    const rulerDom = document.getElementById('ruler')
    if (firstViewer) {
      ruler.setupRulerToggleButton(rulerDom)
    }
    ruler.onCanvasClickSetup(viewer, rulerDom)

    this.onOpen(viewer, initialPosition)

    setupFullscreenButton(viewer, customUIName)
    this.onOpenFailed(viewer, data.viewerId, data.uuid)

    this.setViewportChangeHandler(viewer)

    let analyticsCustomerCode = uiConfiguration.getConfigurationFor(customUIName, 'analyticsCustomerCode')

    if (firstViewer) {
      navigator.setup(viewer)
      ganalytics.setupAnalyticsCustomButtons(data, analyticsCustomerCode)
    }

    ganalytics.setupAnalyticsForOpenSeaDragon(viewer, data, analyticsCustomerCode, firstViewer)

    const hideToolbar = uiConfiguration.getConfigurationFor(customUIName, 'hideToolbar')

    if (hideToolbar) {
      document.getElementById('toolbarContainer').style.transform = 'scale(0)'
    }

    const events = ['mousemove', 'mousedown', 'touchmove']
    events.forEach(event => {
      document.addEventListener(event, () => {
        sendToParent(new Event('user-interacted'))
      })
    })

    return viewer
  },
  onOpen: function (viewer, initialPosition = viewersManipulation.DEFAULT_INITIAL_POSITION) {
    viewer.addHandler('open', event => {
      loader.remove(viewer.id)
      updateWidgetData(event.eventSource.viewport, event.eventSource.source.tilesUrl)
      if (initialPosition.available) {
        const position = new OpenSeadragon.Point(initialPosition.x, initialPosition.y)

        viewer.viewport.zoomTo(initialPosition.zoom, null, false)
        viewer.viewport.panTo(position, false)
      }
    })

    viewer.addHandler('open', () => {
      sendWidgetOpenToParentOnlyAtFirstLoad()
    })

    const viewerElement = document.getElementById(viewer.id)

    viewerElement.addEventListener('changeSelectedImage', e => {
      const state = e.detail.state[viewerElement.dataset.id]

      viewer.open(state.tileSources)

      const viewerPosition = e.detail.viewerPosition
      if (viewerPosition) {
        const numberOfVisibleImages = e.detail.state.filter(s => s.visible).length

        if (numberOfVisibleImages === 3) {
          viewerPosition.width = viewerPosition.width / numberOfVisibleImages
          viewerPosition.x += viewerPosition.width
        } else if (numberOfVisibleImages === 2) {
          viewerPosition.width = viewerPosition.width / numberOfVisibleImages
          viewerPosition.x += viewerPosition.width / 2
        }

        viewer.addHandler('open', () => {
          viewer.viewport.fitBounds(viewerPosition, true)
          if (e.detail.viewerRotation) {
            viewer.viewport.setRotation(e.detail.viewerRotation)
          }
        })
      }
    })

    viewerElement.addEventListener('updateImagesVisibility', e => {
      const state = e.detail.state
      const numberOfVisibleViewers = state.filter(s => s.visible).length
      const id = viewerElement.dataset.id

      const percentage = 100 / numberOfVisibleViewers
      const position = getPosition(parseInt(id), state)

      viewerElement.style['width'] = `${percentage}%`
      viewerElement.style['left'] = `${percentage * position}%`

      const viewerState = state[id]

      if (viewerState.visible) {
        viewerElement.classList.remove('hidden')
      } else {
        viewerElement.classList.add('hidden')
      }
    })
  },

  onOpenFailed: function (viewer, i, imageUuid) {
    viewer.addHandler('open-failed', function (e) {
      loader.remove(viewer.id)
      const errorMessageDiv = document.createElement('div')
      errorMessageDiv.className = 'error-message'
      errorMessageDiv.id = 'error-message-' + i
      client.getErrorMessage(imageUuid).then(message => {
        errorMessageDiv.textContent = message
      })

      viewer.addOverlay({
        element: errorMessageDiv,
        onDraw: function (position, size, element) {
          element.style = ''
        }
      })
      sendToParent(new Event('widget-open-failed'))
    })
  },
  setViewportChangeHandler(viewer) {
    viewer.addHandler('viewport-change', function (e) {
      updateWidgetData(e.eventSource.viewport, e.eventSource.source.tilesUrl)
    })
  }
}
